// types
import type { HTMLProps, JSX } from "react";
import { ButtonColor, ButtonSize } from "@/enums";
import classNames from "classnames";

type AdditionalProps = {
  rightIcon?: any;
  leftIcon?: JSX.Element | string;
  type?: "button" | "submit" | "reset";
  color?: ButtonColor;
  loading?: boolean;
  className?: string;
  buttonSize?: ButtonSize;
  variant?: "outlined-dash" | "outlined-solid";
  id?: string;
};

type ButtonProps = HTMLProps<HTMLButtonElement> & AdditionalProps;

const Button = ({
  rightIcon,
  leftIcon,
  type = "button",
  color = ButtonColor.PRIMARY,
  buttonSize = ButtonSize.MD,
  variant,
  disabled,
  children,
  loading,
  className,
  onClick,
  id,
}: ButtonProps) => {
  const getButtonClasses = (
    color: ButtonColor,
    disabled?: boolean,
    loading?: boolean,
    className?: string,
  ) => {
    const baseClasses =
      "flex h-11 items-center px-4 rounded-[1.9375rem] w-full";

    return classNames(
      baseClasses,
      {
        "primary-bg hover-primary-bg text-white text-sm ":
          color === ButtonColor.PRIMARY &&
          !disabled &&
          // !loading &&
          variant !== "outlined-dash",
        "justify-center text-center": !rightIcon && !leftIcon,
        "justify-between": rightIcon || leftIcon,
        "text-left": rightIcon,
        "font-semibold":
          variant !== "outlined-dash" && color !== ButtonColor.CUSTOM,
        "border-dashed primary-border border bg-white primary-color font-medium uppercase":
          variant === "outlined-dash" && !disabled,
        "secondary-bg hover-secondary-bg primary-color":
          color === ButtonColor.SECONDARY && !disabled,
        "bg-zinc-100 text-zinc-400 cursor-not-allowed": disabled,
        "bg-[#FDF3F3] text-red-600": color === ButtonColor.DANGER,
        "h-[3.125rem]": buttonSize === ButtonSize.SM,
        "h-[3.5rem]": buttonSize === ButtonSize.MD,
        "h-[3.875rem]": buttonSize === ButtonSize.LG,
      },
      className,
    );
  };

  const buttonClasses = getButtonClasses(color, disabled, loading, className);

  return (
    <button
      id={id}
      className={buttonClasses}
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
    >
      {leftIcon && leftIcon}
      {!loading && children}
      {rightIcon && rightIcon}
    </button>
  );
};

export { Button };
